import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface Language {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
}

export const SUPPORTED_LANGUAGES: Language[] = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹' }
];

export const TRANSLATIONS: Record<string, Record<string, any>> = {
  en: {
    NAV: {
      HOME: 'Home',
      COLLECTIONS: 'Collections',
      MARKETPLACE: 'Marketplace',
      EXPLORE: 'Explore',
      CATALOG_OBJECT: 'Catalog Object',
      MY_COLLECTION: 'My Collection',
      OFFERS: 'Offers',
      PROFILE: 'Profile',
      SIGN_IN: 'Sign In',
      SIGN_UP: 'Register',
      SIGN_OUT: 'Sign Out',
      CURRENCY: 'Currency',
      LANGUAGE: 'Language',
      SELECT_LANGUAGE: 'Select Language',
      SELECT_CURRENCY: 'Select Currency',
      JOIN_REGISTRY: 'Join Registry',
      CATALOG_NEW_OBJECT: 'Catalog Object',
      MY_COLLECTION_SUB: 'Manage your private registry and listings',
      OFFERS_SUB: 'Track active offers & custody handovers',
      PROFILE_SUB: 'Account settings, addresses & details',
      VERIFIED_COLLECTOR: 'Verified Collector',
      ASSET_CLASSES: 'Appreciating Asset Classes',
      TAGLINE: 'Living Provenance Registry'
    },
    COMMON: {
      SAVE: 'Save',
      CANCEL: 'Cancel',
      DONE: 'Done',
      EDIT: 'Edit',
      DELETE: 'Delete',
      CLOSE: 'Close',
      SEARCH: 'Search',
      FILTER: 'Filter',
      CLEAR_ALL: 'Clear All',
      LOADING: 'Loading...',
      CONFIRM: 'Confirm',
      ACTIVE: 'Active',
      RECOMMENDED: 'Recommended',
      YES: 'Yes',
      NO: 'No',
      BACK: 'Back',
      ACTIONS: 'Actions'
    },
    STATUS: {
      PUBLIC: 'Public',
      PRIVATE: 'Private (Hidden)',
      PUBLICLY_VISIBLE: 'Publicly Visible',
      PRIVATE_HIDDEN: 'Private / Hidden',
      FOR_SALE: 'Available for Acquisition',
      NOT_FOR_SALE: 'Collection Exhibition (Not for Sale)',
      NOT_OFFERED_FOR_SALE: 'Not Currently Offered for Sale',
      VERIFIED_LINEAGE: 'Verified Lineage',
      MARKETPLACE_ACTIVE: 'Listed for Sale on Marketplace',
      COLLECTION_EXHIBITED: 'Exhibited in Public Collection (Not for Sale)',
      PRIVATE_ARCHIVE: 'Private Archive (Hidden from Public)',
      OWNER_ONLY: 'Owner Only',
      EXHIBITED_PUBLIC_DESC: '🏛️ Exhibited in public collection archive (Not for Sale).',
      ARCHIVED_PRIVATE_DESC: '🔒 Archived in private collection (Hidden from public).'
    },
    CURATION_BAR: {
      MARKETPLACE_DESC: 'This piece is active in marketplace search. Collectors can view pricing and submit purchase offers.',
      PUBLIC_COLLECTION_DESC: 'This piece is exhibited in your public collection. Visitors can view its photos and unbroken provenance history, but cannot purchase it.',
      PRIVATE_ARCHIVE_DESC: 'This piece is in your private archive. It is strictly hidden from all public searches and collection galleries. Only you can view it.',
      CHANGE_STATUS: 'Change Visibility & Status'
    },
    MODAL_VISIBILITY: {
      TITLE: 'Manage Piece Visibility & Pedigree',
      RECOMMENDATION_TITLE: 'Living Pedigree Recommendation: Hide, Don\'t Delete',
      RECOMMENDATION_BODY: 'In Antiqry, every registered artifact maintains an unbroken historical timeline. Rather than permanently deleting and destroying its provenance records, we suggest hiding this piece. It will be removed from marketplace browsing while remaining safely preserved in your private collection archive.',
      CHOICE_1_TITLE: 'Available for Sale on Marketplace',
      CHOICE_1_DESC: 'Listed with your asking price. Active in marketplace search and open for collectors to acquire or make offers.',
      CHOICE_1_BTN: 'List for Sale',
      CHOICE_2_TITLE: 'Exhibited in Public Collection',
      CHOICE_2_DESC: 'Exhibited in your public gallery. Visitors can admire photographs and verify unbroken provenance, but cannot purchase it (Not for Sale).',
      CHOICE_2_BTN: 'Exhibit (Not for Sale)',
      CHOICE_3_TITLE: 'Private Archive (Hidden from Public)',
      CHOICE_3_DESC: 'Strictly hidden from all public searches, marketplace listings, and collection browsing. Only you (the owner) can access it.',
      CHOICE_3_BTN: 'Make Private / Hide',
      DELETE_LINK: 'I still need to permanently delete this record →',
      DELETE_WARNING: 'Permanent deletion cannot be undone. All milestones and AI appraisals for this piece will be permanently destroyed.',
      DELETE_CONFIRM: 'Confirm Permanent Deletion'
    },
    ITEM: {
      ACQUISITION_VALUE: 'Acquisition Value',
      ASKING_PRICE: 'Asking Price',
      MAKE_OFFER: 'Make an Offer',
      ACQUIRE_NOW: 'Acquire Object',
      PLACE_OFFER: 'Submit Binding Offer',
      OFFER_SUCCESS: 'Offer submitted successfully to the collector.',
      PROVENANCE_LINEAGE: 'Verified Provenance Lineage',
      RECORD_MILESTONE: 'Record Historical Milestone',
      ADD_PROVENANCE_BTN: '+ Add Provenance Milestone',
      PEDIGREE_GUARANTEE_TITLE: 'Unbroken Pedigree Guarantee',
      PEDIGREE_GUARANTEE_DESC: 'When an object changes hands on Antiqry, its lifecycle never terminates. All documentation, historical anecdotes, repairs, and past owner records remain permanently attached to this piece for future collectors.',
      CONDITION: 'Condition',
      MATERIALS: 'Materials',
      CATEGORY: 'Category',
      CULTURE: 'Period / Culture',
      MAKER: 'Maker / Brand',
      CURATOR_NOTES: 'Curator Notes',
      ERA: 'Estimated Era',
      HISTORICAL_DESCRIPTION: 'Historical & Visual Description',
      AI_APPRAISAL_REPORT: 'Antiqry AI Expert Curation & Provenance Report',
      RE_APPRAISE: 'Re-Appraise with AI',
      EDIT_LISTING: 'Edit Listing & Curation Details',
      REPLACE_MANAGE_PHOTOS: 'Replace / Manage Photos',
      UPLOAD_PHOTOS: 'Upload Photographs',
      NO_PHOTOS_TITLE: 'Add Object Photographs',
      NO_PHOTOS_DESC: 'Upload clear photographs to showcase this artifact\'s craftsmanship.'
    },
    PHOTO_MODAL: {
      TITLE: 'Manage & Replace Artifact Photographs',
      CURRENT_PHOTOS: 'Current Staged Photos',
      PRIMARY_COVER: '★ Primary Cover',
      MAKE_PRIMARY: 'Make Primary',
      REPLACE_PHOTO: 'Replace Photo',
      DELETE_PHOTO: 'Delete Photo',
      SELECT_PHOTOS_BTN: 'Select Photos from Device',
      DROPZONE_TITLE: 'Add or Replace Photographs',
      DROPZONE_DESC: 'Upload clear, well-lit photos (front, back, details, hallmarks). Mobile camera supported.',
      SAVE_PHOTOS: 'Save Photographs',
      SAVE_REAPPRAISE: 'Save & Re-Appraise with AI'
    },
    CATEGORIES: {
      'Antiques': 'Antiques',
      'Art': 'Art & Paintings',
      'Watches & Horology': 'Watches & Horology',
      'Jewellery & Gemstones': 'Jewellery & Gemstones',
      'Fossils & Natural History': 'Fossils & Natural History',
      'Coins & Numismatics': 'Coins & Numismatics',
      'Sculptures & Bronzes': 'Sculptures & Bronzes',
      'Rare Books & Manuscripts': 'Rare Books & Manuscripts',
      'Silver & Precious Metals': 'Silver & Precious Metals',
      'Clocks & Scientific Instruments': 'Clocks & Scientific Instruments',
      'Armor & Militaria': 'Armor & Militaria',
      'Archaeological Artifacts': 'Archaeological Artifacts',
      'Appreciating Collectibles': 'Appreciating Collectibles'
    },
    COMMERCIAL: {
      TITLE: 'Commercial & Gallery Identity',
      NAME: 'Commercial / Gallery / Store Name',
      NAME_HINT: 'Official name displayed on your cataloged pieces, gallery cards, and physical exhibition tags.',
      ANONYMOUS_TOGGLE: 'Sell & Exhibit Anonymously (Discreet Dealer Mode)',
      ANONYMOUS_HINT: 'Hide your commercial name and personal identity on public object pages and gallery cards. Your verified status and accumulated collector votes/ratings will STILL be visible to buyers, guaranteeing complete trust without exposing your identity.',
      CURATOR_TITLE: 'Exhibiting Gallery / Antiquarian',
      ANONYMOUS_SELLER: 'Private Antiquarian (Discreet Seller)',
      VERIFIED_DEALER: 'Verified Dealer',
      VERIFIED_COLLECTOR: 'Verified Collector',
      POSITIVE_FEEDBACK: 'positive feedback',
      REVIEWS_COUNT: 'verified reviews',
      NO_REVIEWS: 'New to Registry'
    },
    QR_TAG: {
      BUTTON: 'Exhibition QR Tag',
      MODAL_TITLE: 'Museum & Gallery Exhibition Tag',
      MODAL_SUBTITLE: 'Print or display this tag next to your physical artifact in your store, fair booth, or gallery.',
      SCAN_PROMPT: 'Scan with your phone to view unbroken provenance, historical records & acquire this piece on Antiqry.',
      PRINT: 'Print Exhibition Tag',
      CLOSE: 'Close Tag',
      REGISTRY_STANDARD: 'Antiqry Living Provenance Standard'
    },
    REVIEWS: {
      RATE_TRANSACTION: 'Rate Transaction',
      RATE_SELLER: 'Rate Seller',
      RATE_BUYER: 'Rate Buyer',
      YOU_RATED: 'You rated',
      MODAL_TITLE: 'Transaction Feedback & Rating',
      MODAL_SUBTITLE: 'Rate this transaction to build reputation in the Antiqry living custody registry.',
      RATING_LABEL: 'Rating (1 to 5 Stars)',
      COMMENT_LABEL: 'Feedback & Provenance Experience (Optional)',
      COMMENT_PLACEHOLDER: 'Describe item condition, packaging, provenance documentation, prompt dispatch, or communication...',
      SUBMIT: 'Submit Review',
      SUCCESS: 'Thank you! Your verified rating has been recorded.'
    },
    PREFERENCES: {
      TITLE: 'Regional & Display Preferences',
      SUBTITLE: 'Customize your preferred registry language and marketplace valuation currency.',
      LANGUAGE_LABEL: 'Preferred Interface Language',
      LANGUAGE_HINT: 'Used for catalog navigation, historical timelines, and provenance reports.',
      CURRENCY_LABEL: 'Display & Valuation Currency',
      CURRENCY_HINT: 'All secondary market asking prices, estimates, and fee calculators will convert to this currency.',
      SAVED: 'Regional preferences updated successfully.'
    },
    FOOTER: {
      TAGLINE: 'The Global Living Registry for Antiques, Fine Art & Rare Collectibles.',
      ALL_RIGHTS: 'All rights reserved.',
      TERMS: 'Terms of Service',
      PRIVACY: 'Privacy Policy',
      PROVENANCE_STANDARDS: 'Provenance Standards'
    }
  },

  es: {
    NAV: {
      HOME: 'Inicio',
      COLLECTIONS: 'Colecciones',
      MARKETPLACE: 'Mercado',
      EXPLORE: 'Explorar',
      CATALOG_OBJECT: 'Catalogar Objeto',
      MY_COLLECTION: 'Mi Colección',
      OFFERS: 'Ofertas',
      PROFILE: 'Perfil',
      SIGN_IN: 'Iniciar Sesión',
      SIGN_UP: 'Registrarse',
      SIGN_OUT: 'Cerrar Sesión',
      CURRENCY: 'Moneda',
      LANGUAGE: 'Idioma',
      SELECT_LANGUAGE: 'Seleccionar Idioma',
      SELECT_CURRENCY: 'Seleccionar Moneda',
      JOIN_REGISTRY: 'Unirse al Registro',
      CATALOG_NEW_OBJECT: 'Catalogar Objeto',
      MY_COLLECTION_SUB: 'Gestiona tu registro privado y publicaciones',
      OFFERS_SUB: 'Seguimiento de ofertas y traspasos de custodia',
      PROFILE_SUB: 'Configuración de cuenta, direcciones y datos',
      VERIFIED_COLLECTOR: 'Coleccionista Verificado',
      ASSET_CLASSES: 'Clases de Bienes Revalorizables',
      TAGLINE: 'Registro de Procedencia Viva'
    },
    COMMON: {
      SAVE: 'Guardar',
      CANCEL: 'Cancelar',
      DONE: 'Listo',
      EDIT: 'Editar',
      DELETE: 'Eliminar',
      CLOSE: 'Cerrar',
      SEARCH: 'Buscar',
      FILTER: 'Filtrar',
      CLEAR_ALL: 'Limpiar Todo',
      LOADING: 'Cargando...',
      CONFIRM: 'Confirmar',
      ACTIVE: 'Activo',
      RECOMMENDED: 'Recomendado',
      YES: 'Sí',
      NO: 'No',
      BACK: 'Atrás',
      ACTIONS: 'Acciones'
    },
    STATUS: {
      PUBLIC: 'Público',
      PRIVATE: 'Privado (Oculto)',
      PUBLICLY_VISIBLE: 'Visible Públicamente',
      PRIVATE_HIDDEN: 'Privado / Oculto',
      FOR_SALE: 'Disponible para Adquisición',
      NOT_FOR_SALE: 'Colección de Exposición (No a la Venta)',
      NOT_OFFERED_FOR_SALE: 'No disponible a la venta actualmente',
      VERIFIED_LINEAGE: 'Linaje Verificado',
      MARKETPLACE_ACTIVE: 'Publicado a la venta en el Mercado',
      COLLECTION_EXHIBITED: 'Expuesto en Colección Pública (No a la Venta)',
      PRIVATE_ARCHIVE: 'Archivo Privado (Oculto del Público)',
      OWNER_ONLY: 'Solo Propietario',
      EXHIBITED_PUBLIC_DESC: '🏛️ Expuesto en archivo de colección pública (No a la Venta).',
      ARCHIVED_PRIVATE_DESC: '🔒 Preservado en archivo de colección privada (Oculto del público).'
    },
    CURATION_BAR: {
      MARKETPLACE_DESC: 'Esta pieza está activa en las búsquedas del mercado. Los coleccionistas pueden ver el precio y enviar ofertas de compra.',
      PUBLIC_COLLECTION_DESC: 'Esta pieza se exhibe en tu colección pública. Los visitantes pueden admirar sus fotos e historial de procedencia ininterrumpido, pero no pueden adquirirla.',
      PRIVATE_ARCHIVE_DESC: 'Esta pieza está en tu archivo privado. Está estrictamente oculta de búsquedas públicas y galerías. Solo tú puedes verla.',
      CHANGE_STATUS: 'Cambiar Visibilidad y Estado'
    },
    MODAL_VISIBILITY: {
      TITLE: 'Gestionar Visibilidad y Linaje de la Pieza',
      RECOMMENDATION_TITLE: 'Recomendación de Linaje Vivo: Ocultar, No Eliminar',
      RECOMMENDATION_BODY: 'En Antiqry, cada pieza registrada conserva una cronología histórica ininterrumpida. En lugar de eliminar permanentemente y destruir sus registros de procedencia, sugerimos ocultar esta pieza. Se retirará del mercado mientras permanece preservada en tu archivo privado.',
      CHOICE_1_TITLE: 'Disponible para la Venta en el Mercado',
      CHOICE_1_DESC: 'Publicado con tu precio solicitado. Activo en búsquedas de mercado y disponible para que coleccionistas hagan ofertas o compren.',
      CHOICE_1_BTN: 'Poner a la Venta',
      CHOICE_2_TITLE: 'Expuesto en Colección Pública',
      CHOICE_2_DESC: 'Exhibido en tu galería pública. Los visitantes pueden admirar fotografías y verificar la procedencia, pero no pueden comprarlo (No a la Venta).',
      CHOICE_2_BTN: 'Exhibir (No a la Venta)',
      CHOICE_3_TITLE: 'Archivo Privado (Oculto del Público)',
      CHOICE_3_DESC: 'Estrictamente oculto de búsquedas públicas, listados del mercado y colecciones públicas. Solo tú (el propietario) puedes acceder.',
      CHOICE_3_BTN: 'Hacer Privado / Ocultar',
      DELETE_LINK: 'Aún necesito eliminar permanentemente este registro →',
      DELETE_WARNING: 'La eliminación permanente no se puede deshacer. Todos los hitos y valoraciones de IA para esta pieza se destruirán para siempre.',
      DELETE_CONFIRM: 'Confirmar Eliminación Permanente'
    },
    ITEM: {
      ACQUISITION_VALUE: 'Valor de Adquisición',
      ASKING_PRICE: 'Precio Solicitado',
      MAKE_OFFER: 'Hacer una Oferta',
      ACQUIRE_NOW: 'Adquirir Objeto',
      PLACE_OFFER: 'Enviar Oferta Vinculante',
      OFFER_SUCCESS: 'Oferta enviada con éxito al coleccionista.',
      PROVENANCE_LINEAGE: 'Línea de Procedencia Verificada',
      RECORD_MILESTONE: 'Registrar Hito Histórico',
      ADD_PROVENANCE_BTN: '+ Añadir Hito de Procedencia',
      PEDIGREE_GUARANTEE_TITLE: 'Garantía de Pedigrí Ininterrumpido',
      PEDIGREE_GUARANTEE_DESC: 'Cuando un objeto cambia de manos en Antiqry, su ciclo de vida nunca termina. Toda la documentación, anécdotas históricas, restauraciones y registros de antiguos propietarios permanecen permanentemente unidos a esta pieza para futuros coleccionistas.',
      CONDITION: 'Estado de Conservación',
      MATERIALS: 'Materiales',
      CATEGORY: 'Categoría',
      CULTURE: 'Época / Cultura',
      MAKER: 'Autor / Taller / Marca',
      CURATOR_NOTES: 'Notas del Curador',
      ERA: 'Época Estimada',
      HISTORICAL_DESCRIPTION: 'Descripción Histórica y Visual',
      AI_APPRAISAL_REPORT: 'Informe de Curaduría y Procedencia por IA de Antiqry',
      RE_APPRAISE: 'Revaluar con IA',
      EDIT_LISTING: 'Editar Ficha y Detalles de Curaduría',
      REPLACE_MANAGE_PHOTOS: 'Reemplazar / Gestionar Fotos',
      UPLOAD_PHOTOS: 'Subir Fotografías',
      NO_PHOTOS_TITLE: 'Añadir Fotografías del Objeto',
      NO_PHOTOS_DESC: 'Sube fotografías nítidas para mostrar la maestría artesanal de esta pieza.'
    },
    PHOTO_MODAL: {
      TITLE: 'Gestionar y Reemplazar Fotografías del Artefacto',
      CURRENT_PHOTOS: 'Fotos Actuales Seleccionadas',
      PRIMARY_COVER: '★ Portada Principal',
      MAKE_PRIMARY: 'Hacer Portada',
      REPLACE_PHOTO: 'Reemplazar Foto',
      DELETE_PHOTO: 'Eliminar Foto',
      SELECT_PHOTOS_BTN: 'Seleccionar Fotos del Dispositivo',
      DROPZONE_TITLE: 'Añadir o Reemplazar Fotografías',
      DROPZONE_DESC: 'Sube fotos nítidas y bien iluminadas (frontal, trasera, detalles, marcas). Compatible con cámara móvil.',
      SAVE_PHOTOS: 'Guardar Fotografías',
      SAVE_REAPPRAISE: 'Guardar y Revaluar con IA'
    },
    CATEGORIES: {
      'Antiques': 'Antigüedades',
      'Art': 'Arte y Pintura',
      'Watches & Horology': 'Relojería y Horología',
      'Jewellery & Gemstones': 'Joyería y Gemas',
      'Fossils & Natural History': 'Fósiles e Historia Natural',
      'Coins & Numismatics': 'Monedas y Numismática',
      'Sculptures & Bronzes': 'Esculturas y Bronces',
      'Rare Books & Manuscripts': 'Libros Raros y Manuscritos',
      'Silver & Precious Metals': 'Platería y Metales Preciosos',
      'Clocks & Scientific Instruments': 'Relojes e Instrumentos Científicos',
      'Armor & Militaria': 'Armaduras y Militaria',
      'Archaeological Artifacts': 'Artefactos Arqueológicos',
      'Appreciating Collectibles': 'Coleccionables de Valor Creciente'
    },
    COMMERCIAL: {
      TITLE: 'Identidad Comercial y Galería',
      NAME: 'Nombre Comercial / Galería / Tienda',
      NAME_HINT: 'Nombre oficial que aparecerá en tus piezas catalogadas, tarjetas de galería y etiquetas de exposición física.',
      ANONYMOUS_TOGGLE: 'Vender y Exponer de Forma Anónima (Modo Coleccionista Discreto)',
      ANONYMOUS_HINT: 'Oculta tu nombre comercial e identidad personal de las páginas públicas y etiquetas. Tu estado verificado y tus valoraciones/votos acumulados SEGUIRÁN visibles para los compradores, garantizando total confianza sin exponer tu identidad.',
      CURATOR_TITLE: 'Galería Expositora / Anticuario',
      ANONYMOUS_SELLER: 'Anticuario Privado (Vendedor Discreto)',
      VERIFIED_DEALER: 'Comerciante Verificado',
      VERIFIED_COLLECTOR: 'Coleccionista Verificado',
      POSITIVE_FEEDBACK: 'votos positivos',
      REVIEWS_COUNT: 'valoraciones verificadas',
      NO_REVIEWS: 'Nuevo en el Registro'
    },
    QR_TAG: {
      BUTTON: 'Etiqueta QR de Exposición',
      MODAL_TITLE: 'Etiqueta de Exposición para Galería o Tienda',
      MODAL_SUBTITLE: 'Imprime o coloca esta tarjeta junto a tu pieza física en tu tienda, feria o vitrina de galería.',
      SCAN_PROMPT: 'Escanea con tu teléfono para ver la procedencia ininterrumpida, historial y adquirir esta pieza en Antiqry.',
      PRINT: 'Imprimir Etiqueta',
      CLOSE: 'Cerrar Etiqueta',
      REGISTRY_STANDARD: 'Estándar de Procedencia Viva Antiqry'
    },
    REVIEWS: {
      RATE_TRANSACTION: 'Valorar Transacción',
      RATE_SELLER: 'Valorar Vendedor',
      RATE_BUYER: 'Valorar Comprador',
      YOU_RATED: 'Has valorado',
      MODAL_TITLE: 'Valoración y Opinión de la Transacción',
      MODAL_SUBTITLE: 'Valora esta transacción para construir reputación en el registro de custodia viva de Antiqry.',
      RATING_LABEL: 'Puntuación (1 a 5 Estrellas)',
      COMMENT_LABEL: 'Comentario y Experiencia de Procedencia (Opcional)',
      COMMENT_PLACEHOLDER: 'Describe el estado de la pieza, embalaje, documentación histórica, rapidez de envío o comunicación...',
      SUBMIT: 'Enviar Valoración',
      SUCCESS: '¡Gracias! Tu valoración verificada ha sido registrada.'
    },
    PREFERENCES: {
      TITLE: 'Preferencias Regionales y de Visualización',
      SUBTITLE: 'Personalice el idioma del registro y la moneda de valoración del mercado.',
      LANGUAGE_LABEL: 'Idioma Preferido de la Interfaz',
      LANGUAGE_HINT: 'Utilizado para navegación en el catálogo, líneas de tiempo históricas e informes de procedencia.',
      CURRENCY_LABEL: 'Moneda de Visualización y Valoración',
      CURRENCY_HINT: 'Todos los precios de venta, estimaciones y calculadoras de tarifas se convertirán a esta moneda.',
      SAVED: 'Preferencias regionales actualizadas con éxito.'
    },
    FOOTER: {
      TAGLINE: 'El Registro Global Vivo para Antigüedades, Bellas Artes y Piezas de Colección.',
      ALL_RIGHTS: 'Todos los derechos reservados.',
      TERMS: 'Términos de Servicio',
      PRIVACY: 'Política de Privacidad',
      PROVENANCE_STANDARDS: 'Estándares de Procedencia'
    }
  },

  fr: {
    NAV: {
      HOME: 'Accueil',
      COLLECTIONS: 'Collections',
      MARKETPLACE: 'Marché',
      EXPLORE: 'Explorer',
      CATALOG_OBJECT: 'Cataloguer un Objet',
      MY_COLLECTION: 'Ma Collection',
      OFFERS: 'Offres',
      PROFILE: 'Profil',
      SIGN_IN: 'Connexion',
      SIGN_UP: 'S\'inscrire',
      SIGN_OUT: 'Déconnexion',
      CURRENCY: 'Devise',
      LANGUAGE: 'Langue',
      SELECT_LANGUAGE: 'Choisir la Langue',
      SELECT_CURRENCY: 'Choisir la Devise',
      JOIN_REGISTRY: 'Rejoindre le Registre',
      CATALOG_NEW_OBJECT: 'Cataloguer un Objet',
      MY_COLLECTION_SUB: 'Gérez votre registre privé et vos annonces',
      OFFERS_SUB: 'Suivi des offres et transferts de garde',
      PROFILE_SUB: 'Paramètres du compte, adresses et détails',
      VERIFIED_COLLECTOR: 'Collectionneur Vérifié',
      ASSET_CLASSES: 'Classes d\'Actifs d\'Art',
      TAGLINE: 'Registre de Provenance Vivant'
    },
    COMMON: {
      SAVE: 'Enregistrer',
      CANCEL: 'Annuler',
      DONE: 'Terminé',
      EDIT: 'Modifier',
      DELETE: 'Supprimer',
      CLOSE: 'Fermer',
      SEARCH: 'Rechercher',
      FILTER: 'Filtrer',
      CLEAR_ALL: 'Tout effacer',
      LOADING: 'Chargement...',
      CONFIRM: 'Confirmer',
      ACTIVE: 'Actif',
      RECOMMENDED: 'Recommandé',
      YES: 'Oui',
      NO: 'Non',
      BACK: 'Retour',
      ACTIONS: 'Actions'
    },
    STATUS: {
      PUBLIC: 'Public',
      PRIVATE: 'Privé (Masqué)',
      PUBLICLY_VISIBLE: 'Visible Publiquement',
      PRIVATE_HIDDEN: 'Privé / Masqué',
      FOR_SALE: 'Disponible à l\'Acquisition',
      NOT_FOR_SALE: 'Collection d\'Exposition (Non à Vendre)',
      NOT_OFFERED_FOR_SALE: 'Actuellement non proposé à la vente',
      VERIFIED_LINEAGE: 'Lignée Vérifiée',
      MARKETPLACE_ACTIVE: 'En vente sur le Marché',
      COLLECTION_EXHIBITED: 'Exposé en Collection Publique (Non à Vendre)',
      PRIVATE_ARCHIVE: 'Archive Privée (Masquée du Public)',
      OWNER_ONLY: 'Propriétaire Uniquement',
      EXHIBITED_PUBLIC_DESC: '🏛️ Exposé en archive de collection publique (Non à Vendre).',
      ARCHIVED_PRIVATE_DESC: '🔒 Préservé dans l\'archive de collection privée (Masqué du public).'
    },
    CURATION_BAR: {
      MARKETPLACE_DESC: 'Cette pièce est active sur le marché public. Les collectionneurs peuvent voir son prix et soumettre des offres d\'achat.',
      PUBLIC_COLLECTION_DESC: 'Cette pièce est exposée dans votre collection publique. Les visiteurs peuvent admirer ses photos et son historique de provenance, mais ne peuvent pas l\'acheter.',
      PRIVATE_ARCHIVE_DESC: 'Cette pièce est conservée dans votre archive privée. Elle est strictement masquée de toutes les recherches publiques. Vous seul pouvez la voir.',
      CHANGE_STATUS: 'Modifier la Visibilité et le Statut'
    },
    MODAL_VISIBILITY: {
      TITLE: 'Gérer la Visibilité et la Lignée de la Pièce',
      RECOMMENDATION_TITLE: 'Recommandation de Lignée Vivante : Masquer, Ne Pas Supprimer',
      RECOMMENDATION_BODY: 'Sur Antiqry, chaque objet enregistré maintient une chronologie historique ininterrompue. Plutôt que de supprimer définitivement et détruire ses archives de provenance, nous vous conseillons de masquer cette pièce. Elle sera retirée du marché tout en restant préservée dans vos archives privées.',
      CHOICE_1_TITLE: 'Disponible à la Vente sur le Marché',
      CHOICE_1_DESC: 'Affiché avec votre prix demandé. Actif dans les recherches publiques pour l\'achat direct ou les offres de collectionneurs.',
      CHOICE_1_BTN: 'Mettre en Vente',
      CHOICE_2_TITLE: 'Exposé en Collection Publique',
      CHOICE_2_DESC: 'Exposé dans votre galerie publique. Les visiteurs peuvent admirer les photographies et vérifier la traçabilité, sans pouvoir l\'acheter (Non à Vendre).',
      CHOICE_2_BTN: 'Exposer (Non à Vendre)',
      CHOICE_3_TITLE: 'Archive Privée (Masquée du Public)',
      CHOICE_3_DESC: 'Strictement masqué de toutes les recherches publiques, listes de vente et galeries. Vous seul (le propriétaire) y avez accès.',
      CHOICE_3_BTN: 'Rendre Privé / Masquer',
      DELETE_LINK: 'Je dois quand même supprimer définitivement cet enregistrement →',
      DELETE_WARNING: 'La suppression définitive est irréversible. Tous les jalons historiques et expertises IA attachés à cette pièce seront détruits.',
      DELETE_CONFIRM: 'Confirmer la Suppression Définitive'
    },
    ITEM: {
      ACQUISITION_VALUE: 'Valeur d\'Acquisition',
      ASKING_PRICE: 'Prix Demandé',
      MAKE_OFFER: 'Faire une Offre',
      ACQUIRE_NOW: 'Acquérir l\'Objet',
      PLACE_OFFER: 'Soumettre une Offre Ferme',
      OFFER_SUCCESS: 'Offre transmise avec succès au collectionneur.',
      PROVENANCE_LINEAGE: 'Lignée de Provenance Vérifiée',
      RECORD_MILESTONE: 'Enregistrer un Jalon Historique',
      ADD_PROVENANCE_BTN: '+ Ajouter un Jalon de Provenance',
      PEDIGREE_GUARANTEE_TITLE: 'Garantie de Pédigrée Ininterrompu',
      PEDIGREE_GUARANTEE_DESC: 'Lorsqu\'un objet change de mains sur Antiqry, son cycle de vie ne s\'interrompt jamais. Toute sa documentation, ses anecdotes historiques, ses restaurations et les traces de ses anciens propriétaires restent attachés à la pièce pour les générations futures.',
      CONDITION: 'État de Conservation',
      MATERIALS: 'Matériaux',
      CATEGORY: 'Catégorie',
      CULTURE: 'Époque / Culture',
      MAKER: 'Artiste / Atelier / Fabricant',
      CURATOR_NOTES: 'Notes du Conservateur',
      ERA: 'Époque Estimée',
      HISTORICAL_DESCRIPTION: 'Description Historique et Visuelle',
      AI_APPRAISAL_REPORT: 'Rapport d\'Expertise et d\'Authenticité IA Antiqry',
      RE_APPRAISE: 'Ré-expertiser avec l\'IA',
      EDIT_LISTING: 'Modifier la Fiche & Détails de Curation',
      REPLACE_MANAGE_PHOTOS: 'Remplacer / Gérer les Photos',
      UPLOAD_PHOTOS: 'Téléverser des Photos',
      NO_PHOTOS_TITLE: 'Ajouter des Photos de l\'Objet',
      NO_PHOTOS_DESC: 'Téléversez des photos nettes pour mettre en valeur le savoir-faire artisanal de cet objet.'
    },
    PHOTO_MODAL: {
      TITLE: 'Gérer et Remplacer les Photos de l\'Objet',
      CURRENT_PHOTOS: 'Photos Actuellement Retenues',
      PRIMARY_COVER: '★ Couverture Principale',
      MAKE_PRIMARY: 'Définir en Couverture',
      REPLACE_PHOTO: 'Remplacer la Photo',
      DELETE_PHOTO: 'Supprimer la Photo',
      SELECT_PHOTOS_BTN: 'Sélectionner des Photos sur l\'Appareil',
      DROPZONE_TITLE: 'Ajouter ou Remplacer des Photos',
      DROPZONE_DESC: 'Téléversez des photographies claires et bien éclairées (face, dos, détails, poinçons). Prise de vue par appareil photo mobile prise en charge.',
      SAVE_PHOTOS: 'Enregistrer les Photos',
      SAVE_REAPPRAISE: 'Enregistrer et Ré-expertiser avec l\'IA'
    },
    CATEGORIES: {
      'Antiques': 'Antiquités',
      'Art': 'Art & Peinture',
      'Watches & Horology': 'Horlogerie & Montres',
      'Jewellery & Gemstones': 'Joaillerie & Pierres Précieuses',
      'Fossils & Natural History': 'Fossiles & Histoire Naturelle',
      'Coins & Numismatics': 'Monnaies & Numismatique',
      'Sculptures & Bronzes': 'Sculptures & Bronzes',
      'Rare Books & Manuscripts': 'Livres Rares & Manuscrits',
      'Silver & Precious Metals': 'Argenterie & Métaux Précieux',
      'Clocks & Scientific Instruments': 'Horloges & Instruments Scientifiques',
      'Armor & Militaria': 'Armes Anciennes & Militaria',
      'Archaeological Artifacts': 'Artefacts Archéologiques',
      'Appreciating Collectibles': 'Objets de Collection Rares'
    },
    COMMERCIAL: {
      TITLE: 'Identité Commerciale & Galerie',
      NAME: 'Nom Commercial / Galerie / Boutique',
      NAME_HINT: 'Nom officiel affiché sur vos pièces cataloguées, fiches de galerie et étiquettes d\'exposition physique.',
      ANONYMOUS_TOGGLE: 'Vendre et Exposer de Façon Anonyme (Mode Discret)',
      ANONYMOUS_HINT: 'Masquez votre nom commercial et votre identité personnelle sur les pages publiques. Votre statut vérifié et vos avis/notes accumulés RESTERONT visibles par les acheteurs, garantissant une entière confiance sans révéler votre identité.',
      CURATOR_TITLE: 'Galerie Exposante / Antiquaire',
      ANONYMOUS_SELLER: 'Antiquaire Privé (Vendeur Discret)',
      VERIFIED_DEALER: 'Marchand Vérifié',
      VERIFIED_COLLECTOR: 'Collectionneur Vérifié',
      POSITIVE_FEEDBACK: 'avis positifs',
      REVIEWS_COUNT: 'évaluations vérifiées',
      NO_REVIEWS: 'Nouveau sur le Registre'
    },
    QR_TAG: {
      BUTTON: 'Étiquette QR d\'Exposition',
      MODAL_TITLE: 'Étiquette d\'Exposition Musée & Galerie',
      MODAL_SUBTITLE: 'Imprimez ou disposez cette fiche à côté de votre objet dans votre boutique, stand ou vitrine.',
      SCAN_PROMPT: 'Scannez avec votre téléphone pour voir la provenance ininterrompue, les archives et acquérir cet objet sur Antiqry.',
      PRINT: 'Imprimer l\'Étiquette',
      CLOSE: 'Fermer',
      REGISTRY_STANDARD: 'Standard de Provenance Vivante Antiqry'
    },
    REVIEWS: {
      RATE_TRANSACTION: 'Évaluer la Transaction',
      RATE_SELLER: 'Évaluer le Vendeur',
      RATE_BUYER: 'Évaluer l\'Acheteur',
      YOU_RATED: 'Vous avez noté',
      MODAL_TITLE: 'Évaluation & Avis de la Transaction',
      MODAL_SUBTITLE: 'Évaluez cette transaction pour renforcer la réputation dans le registre de garde Antiqry.',
      RATING_LABEL: 'Note (1 à 5 Étoiles)',
      COMMENT_LABEL: 'Commentaire & Expérience (Optionnel)',
      COMMENT_PLACEHOLDER: 'Décrivez l\'état de l\'objet, l\'emballage, les documents de provenance, l\'expédition...',
      SUBMIT: 'Envoyer l\'Évaluation',
      SUCCESS: 'Merci ! Votre évaluation vérifiée a été enregistrée.'
    },
    PREFERENCES: {
      TITLE: 'Préférences Régionales et d\'Affichage',
      SUBTITLE: 'Personnalisez votre langue de registre préférée et la devise d\'évaluation.',
      LANGUAGE_LABEL: 'Langue Préférée de l\'Interface',
      LANGUAGE_HINT: 'Utilisée pour la navigation dans le catalogue, les chronologies et les rapports de provenance.',
      CURRENCY_LABEL: 'Devise d\'Affichage et d\'Évaluation',
      CURRENCY_HINT: 'Tous les prix, estimations et calculateurs seront convertis dans cette devise.',
      SAVED: 'Préférences régionales mises à jour avec succès.'
    },
    FOOTER: {
      TAGLINE: 'Le Registre Mondial Vivant pour les Antiquités, les Beaux-Arts et les Objets de Collection.',
      ALL_RIGHTS: 'Tous droits réservés.',
      TERMS: 'Conditions d\'Utilisation',
      PRIVACY: 'Politique de Confidentialité',
      PROVENANCE_STANDARDS: 'Normes de Provenance'
    }
  },

  de: {
    NAV: {
      HOME: 'Startseite',
      COLLECTIONS: 'Sammlungen',
      MARKETPLACE: 'Marktplatz',
      EXPLORE: 'Entdecken',
      CATALOG_OBJECT: 'Objekt Katalogisieren',
      MY_COLLECTION: 'Meine Sammlung',
      OFFERS: 'Angebote',
      PROFILE: 'Profil',
      SIGN_IN: 'Anmelden',
      SIGN_UP: 'Registrieren',
      SIGN_OUT: 'Abmelden',
      CURRENCY: 'Währung',
      LANGUAGE: 'Sprache',
      SELECT_LANGUAGE: 'Sprache Wählen',
      SELECT_CURRENCY: 'Währung Wählen',
      JOIN_REGISTRY: 'Dem Register Beitreten',
      CATALOG_NEW_OBJECT: 'Objekt Katalogisieren',
      MY_COLLECTION_SUB: 'Verwalten Sie Ihre Sammlung und Angebote',
      OFFERS_SUB: 'Aktive Angebote und Eigentumsübergänge verfolgen',
      PROFILE_SUB: 'Kontoeinstellungen, Adressen & Details',
      VERIFIED_COLLECTOR: 'Verifizierter Sammler',
      ASSET_CLASSES: 'Wertsteigernde Anlageklassen',
      TAGLINE: 'Lebendiges Provenienzregister'
    },
    COMMON: {
      SAVE: 'Speichern',
      CANCEL: 'Abbrechen',
      DONE: 'Fertig',
      EDIT: 'Bearbeiten',
      DELETE: 'Löschen',
      CLOSE: 'Schließen',
      SEARCH: 'Suchen',
      FILTER: 'Filtern',
      CLEAR_ALL: 'Alles löschen',
      LOADING: 'Laden...',
      CONFIRM: 'Bestätigen',
      ACTIVE: 'Aktiv',
      RECOMMENDED: 'Empfohlen',
      YES: 'Ja',
      NO: 'Nein',
      BACK: 'Zurück',
      ACTIONS: 'Aktionen'
    },
    STATUS: {
      PUBLIC: 'Öffentlich',
      PRIVATE: 'Privat (Ausgeblendet)',
      PUBLICLY_VISIBLE: 'Öffentlich Sichtbar',
      PRIVATE_HIDDEN: 'Privat / Ausgeblendet',
      FOR_SALE: 'Zum Erwerb Verfügbar',
      NOT_FOR_SALE: 'Ausstellungssammlung (Nicht Käuflich)',
      NOT_OFFERED_FOR_SALE: 'Derzeit nicht zum Verkauf angeboten',
      VERIFIED_LINEAGE: 'Verifizierte Abstammung',
      MARKETPLACE_ACTIVE: 'Auf dem Marktplatz zum Verkauf gelistet',
      COLLECTION_EXHIBITED: 'In öffentlicher Sammlung ausgestellt (Nicht Käuflich)',
      PRIVATE_ARCHIVE: 'Privatarchiv (Vor Öffentlichkeit verborgen)',
      OWNER_ONLY: 'Nur Eigentümer',
      EXHIBITED_PUBLIC_DESC: '🏛️ In öffentlichem Sammlungsarchiv ausgestellt (Nicht Käuflich).',
      ARCHIVED_PRIVATE_DESC: '🔒 Im privaten Sammlungsarchiv verwahrt (Vor Öffentlichkeit verborgen).'
    },
    CURATION_BAR: {
      MARKETPLACE_DESC: 'Dieses Stück ist im öffentlichen Marktplatz aktiv. Sammler können den Preis einsehen und Kaufangebote unterbreiten.',
      PUBLIC_COLLECTION_DESC: 'Dieses Stück wird in Ihrer öffentlichen Sammlung ausgestellt. Besucher können Fotos und die lückenlose Provenienz bewundern, es jedoch nicht kaufen.',
      PRIVATE_ARCHIVE_DESC: 'Dieses Stück befindet sich in Ihrem privaten Archiv. Es ist vor allen öffentlichen Suchanfragen und Sammlungsübersichten verborgen.',
      CHANGE_STATUS: 'Sichtbarkeit & Status ändern'
    },
    MODAL_VISIBILITY: {
      TITLE: 'Sichtbarkeit & Provenienz des Objekts Verwalten',
      RECOMMENDATION_TITLE: 'Empfehlung des Lebendigen Registers: Verbergen, Nicht Löschen',
      RECOMMENDATION_BODY: 'Bei Antiqry behält jedes registrierte Kunstwerk eine lückenlose historische Zeitlinie. Anstatt den Datensatz unwiderruflich zu löschen und seine Dokumente zu vernichten, empfehlen wir das Verbergen. Das Objekt wird aus dem Marktplatz entfernt und verbleibt sicher in Ihrem Privatarchiv.',
      CHOICE_1_TITLE: 'Auf dem Marktplatz zum Verkauf Angeboten',
      CHOICE_1_DESC: 'Mit Ihrem Preis gelistet. In der Marktplatzsuche aktiv und für Sammler zur Direktakquise oder für Angebote freigegeben.',
      CHOICE_1_BTN: 'Zum Verkauf listen',
      CHOICE_2_TITLE: 'In Öffentlicher Sammlung Ausgestellt',
      CHOICE_2_DESC: 'In Ihrer öffentlichen Galerie ausgestellt. Besucher können Fotos und Provenienz begutachten, das Objekt ist jedoch unverkäuflich (Nicht Käuflich).',
      CHOICE_2_BTN: 'Ausstellen (Nicht Käuflich)',
      CHOICE_3_TITLE: 'Privates Archiv (Vor Öffentlichkeit Verborgen)',
      CHOICE_3_DESC: 'Streng verborgen vor öffentlichen Suchanfragen, Marktplatzlisten und Sammlungen. Nur Sie als Eigentümer haben Zugriff.',
      CHOICE_3_BTN: 'Privat Schalten / Verbergen',
      DELETE_LINK: 'Ich möchte diesen Datensatz trotzdem dauerhaft löschen →',
      DELETE_WARNING: 'Dauerhaftes Löschen kann nicht rückgängig gemacht werden. Alle historischen Meilensteine und KI-Gutachten werden unwiederbringlich zerstört.',
      DELETE_CONFIRM: 'Dauerhafte Löschung Bestätigen'
    },
    ITEM: {
      ACQUISITION_VALUE: 'Erwerbswert',
      ASKING_PRICE: 'Angebotspreis',
      MAKE_OFFER: 'Angebot Machen',
      ACQUIRE_NOW: 'Objekt Erwerben',
      PLACE_OFFER: 'Verbindliches Gebot Abgeben',
      OFFER_SUCCESS: 'Angebot erfolgreich an den Sammler übermittelt.',
      PROVENANCE_LINEAGE: 'Verifizierte Provenienzlinie',
      RECORD_MILESTONE: 'Historischen Meilenstein Erfassen',
      ADD_PROVENANCE_BTN: '+ Provenienz-Meilenstein Hinzufügen',
      PEDIGREE_GUARANTEE_TITLE: 'Garantie Lückenloser Historie',
      PEDIGREE_GUARANTEE_DESC: 'Wenn ein Kunstwerk auf Antiqry den Besitzer wechselt, endet seine Geschichte niemals. Alle Dokumente, Überlieferungen, Reparaturen und Vorbesitzerdaten bleiben für künftige Sammler untrennbar mit dem Objekt verknüpft.',
      CONDITION: 'Erhaltungszustand',
      MATERIALS: 'Materialien',
      CATEGORY: 'Kategorie',
      CULTURE: 'Epoche / Kulturkreis',
      MAKER: 'Künstler / Manufaktur / Meister',
      CURATOR_NOTES: 'Kuratorische Notizen',
      ERA: 'Geschätzte Datierung',
      HISTORICAL_DESCRIPTION: 'Historische & Visuelle Beschreibung',
      AI_APPRAISAL_REPORT: 'Antiqry KI-Kurator- & Provenienzbericht',
      RE_APPRAISE: 'Mit KI Neu Bewerten',
      EDIT_LISTING: 'Katalogeintrag & Kurationsdaten Bearbeiten',
      REPLACE_MANAGE_PHOTOS: 'Fotos Ersetzen / Verwalten',
      UPLOAD_PHOTOS: 'Fotografien Hochladen',
      NO_PHOTOS_TITLE: 'Objektfotografien Hinzufügen',
      NO_PHOTOS_DESC: 'Laden Sie aussagekräftige Fotografien hoch, um die handwerkliche Qualität dieses Kunstwerks zu präsentieren.'
    },
    PHOTO_MODAL: {
      TITLE: 'Objektfotografien Verwalten & Ersetzen',
      CURRENT_PHOTOS: 'Aktuell Ausgewählte Fotos',
      PRIMARY_COVER: '★ Titelbild',
      MAKE_PRIMARY: 'Als Titelbild Festlegen',
      REPLACE_PHOTO: 'Foto Ersetzen',
      DELETE_PHOTO: 'Foto Löschen',
      SELECT_PHOTOS_BTN: 'Fotos vom Gerät Auswählen',
      DROPZONE_TITLE: 'Fotos Hinzufügen oder Ersetzen',
      DROPZONE_DESC: 'Laden Sie scharfe, gut beleuchtete Fotos hoch (Vorderseite, Rückseite, Details, Stempel/Marken). Smartphone-Kamera wird unterstützt.',
      SAVE_PHOTOS: 'Fotografien Speichern',
      SAVE_REAPPRAISE: 'Speichern & mit KI Neu Bewerten'
    },
    CATEGORIES: {
      'Antiques': 'Antiquitäten',
      'Art': 'Kunst & Malerei',
      'Watches & Horology': 'Uhren & Horologie',
      'Jewellery & Gemstones': 'Schmuck & Edelsteine',
      'Fossils & Natural History': 'Fossilien & Naturgeschichte',
      'Coins & Numismatics': 'Münzen & Numismatik',
      'Sculptures & Bronzes': 'Skulpturen & Bronzen',
      'Rare Books & Manuscripts': 'Seltene Bücher & Handschriften',
      'Silver & Precious Metals': 'Silber & Edelmetalle',
      'Clocks & Scientific Instruments': 'Großuhren & Wissenschaftliche Instrumente',
      'Armor & Militaria': 'Rüstungen & Militaria',
      'Archaeological Artifacts': 'Archäologische Funde',
      'Appreciating Collectibles': 'Wertbeständige Sammlerobjekte'
    },
    COMMERCIAL: {
      TITLE: 'Gewerbliche Identität & Galerie',
      NAME: 'Handelsname / Galerie / Geschäftsname',
      NAME_HINT: 'Offizieller Name auf katalogisierten Objekten, Galeriekarten und Ausstellungsanhängern.',
      ANONYMOUS_TOGGLE: 'Anonym Verkaufen & Ausstellen (Diskreter Händlermodus)',
      ANONYMOUS_HINT: 'Verbergen Sie Ihren Namen auf öffentlichen Seiten. Ihr Verifizierungsstatus und Ihre gesammelten Bewertungen/Stimmen BLEIBEN für Käufer sichtbar, was volles Vertrauen schafft, ohne Ihre Identität preiszugeben.',
      CURATOR_TITLE: 'Ausstellende Galerie / Antiquar',
      ANONYMOUS_SELLER: 'Privater Antiquar (Diskreter Verkäufer)',
      VERIFIED_DEALER: 'Verifizierter Händler',
      VERIFIED_COLLECTOR: 'Verifizierter Sammler',
      POSITIVE_FEEDBACK: 'positive Bewertungen',
      REVIEWS_COUNT: 'verifizierte Bewertungen',
      NO_REVIEWS: 'Neu im Register'
    },
    QR_TAG: {
      BUTTON: 'Ausstellungs-QR-Etikett',
      MODAL_TITLE: 'Museums- & Galerie-Ausstellungsschild',
      MODAL_SUBTITLE: 'Drucken oder platzieren Sie dieses Etikett neben Ihrem physischen Objekt in Geschäft oder Galerie.',
      SCAN_PROMPT: 'Mit dem Smartphone scannen, um die lückenlose Provenienz zu prüfen und das Objekt auf Antiqry zu erwerben.',
      PRINT: 'Etikett Drucken',
      CLOSE: 'Schließen',
      REGISTRY_STANDARD: 'Antiqry Lebendiger Provenienzstandard'
    },
    REVIEWS: {
      RATE_TRANSACTION: 'Transaktion Bewerten',
      RATE_SELLER: 'Verkäufer Bewerten',
      RATE_BUYER: 'Käufer Bewerten',
      YOU_RATED: 'Sie haben bewertet mit',
      MODAL_TITLE: 'Transaktionsbewertung & Feedback',
      MODAL_SUBTITLE: 'Bewerten Sie diese Transaktion, um das Vertrauen im Antiqry-Provenienzregister zu stärken.',
      RATING_LABEL: 'Bewertung (1 bis 5 Sterne)',
      COMMENT_LABEL: 'Erfahrungsbericht (Optional)',
      COMMENT_PLACEHOLDER: 'Zustand des Objekts, Verpackung, historische Dokumentation, Lieferzeit oder Kommunikation...',
      SUBMIT: 'Bewertung Absenden',
      SUCCESS: 'Vielen Dank! Ihre verifizierte Bewertung wurde gespeichert.'
    },
    PREFERENCES: {
      TITLE: 'Regionale Einstellungen & Anzeige',
      SUBTITLE: 'Passen Sie Ihre bevorzugte Registersprache und Marktwährung an.',
      LANGUAGE_LABEL: 'Bevorzugte Sprache',
      LANGUAGE_HINT: 'Wird für Katalognavigation, historische Zeitleisten und Provenienzberichte verwendet.',
      CURRENCY_LABEL: 'Anzeige- & Bewertungswährung',
      CURRENCY_HINT: 'Alle Angebotspreise, Schätzungen und Gebührenrechner werden in diese Währung umgerechnet.',
      SAVED: 'Regionale Einstellungen erfolgreich aktualisiert.'
    },
    FOOTER: {
      TAGLINE: 'Das Globale Lebendige Register für Antiquitäten, Bildende Kunst & Seltene Sammlerstücke.',
      ALL_RIGHTS: 'Alle Rechte vorbehalten.',
      TERMS: 'Nutzungsbedingungen',
      PRIVACY: 'Datenschutzerklärung',
      PROVENANCE_STANDARDS: 'Provenienzstandards'
    }
  },

  it: {
    NAV: {
      HOME: 'Home',
      COLLECTIONS: 'Collezioni',
      MARKETPLACE: 'Mercato',
      EXPLORE: 'Esplora',
      CATALOG_OBJECT: 'Cataloga Oggetto',
      MY_COLLECTION: 'La Mia Collezione',
      OFFERS: 'Offerte',
      PROFILE: 'Profilo',
      SIGN_IN: 'Accedi',
      SIGN_UP: 'Registrati',
      SIGN_OUT: 'Esci',
      CURRENCY: 'Valuta',
      LANGUAGE: 'Lingua',
      SELECT_LANGUAGE: 'Seleziona Lingua',
      SELECT_CURRENCY: 'Seleziona Valuta',
      JOIN_REGISTRY: 'Unisciti al Registro',
      CATALOG_NEW_OBJECT: 'Cataloga Oggetto',
      MY_COLLECTION_SUB: 'Gestisci la tua collezione e annunci',
      OFFERS_SUB: 'Traccia offerte attive e passaggi di custodia',
      PROFILE_SUB: 'Impostazioni account, indirizzi e dettagli',
      VERIFIED_COLLECTOR: 'Collezionista Verificato',
      ASSET_CLASSES: 'Classi di Beni Pregiati',
      TAGLINE: 'Registro di Provenienza Vivente'
    },
    COMMON: {
      SAVE: 'Salva',
      CANCEL: 'Annulla',
      DONE: 'Fatto',
      EDIT: 'Modifica',
      DELETE: 'Elimina',
      CLOSE: 'Chiudi',
      SEARCH: 'Cerca',
      FILTER: 'Filtra',
      CLEAR_ALL: 'Cancella Tutto',
      LOADING: 'Caricamento...',
      CONFIRM: 'Conferma',
      ACTIVE: 'Attivo',
      RECOMMENDED: 'Consigliato',
      YES: 'Sì',
      NO: 'No',
      BACK: 'Indietro',
      ACTIONS: 'Azioni'
    },
    STATUS: {
      PUBLIC: 'Pubblico',
      PRIVATE: 'Privato (Nascosto)',
      PUBLICLY_VISIBLE: 'Visibile Pubblicamente',
      PRIVATE_HIDDEN: 'Privato / Nascosto',
      FOR_SALE: 'Disponibile per l\'Acquisizione',
      NOT_FOR_SALE: 'Collezione in Mostra (Non in Vendita)',
      NOT_OFFERED_FOR_SALE: 'Attualmente non offerto in vendita',
      VERIFIED_LINEAGE: 'Lignaggio Verificato',
      MARKETPLACE_ACTIVE: 'In vendita sul Mercato',
      COLLECTION_EXHIBITED: 'Esposto in Collezione Pubblica (Non in Vendita)',
      PRIVATE_ARCHIVE: 'Archivio Privato (Nascosto al Pubblico)',
      OWNER_ONLY: 'Solo Proprietario',
      EXHIBITED_PUBLIC_DESC: '🏛️ Esposto nell\'archivio della collezione pubblica (Non in Vendita).',
      ARCHIVED_PRIVATE_DESC: '🔒 Custodito nell\'archivio della collezione privata (Nascosto al pubblico).'
    },
    CURATION_BAR: {
      MARKETPLACE_DESC: 'Questo pezzo è attivo nelle ricerche del mercato. I collezionisti possono vedere il prezzo e presentare offerte d\'acquisto.',
      PUBLIC_COLLECTION_DESC: 'Questo pezzo è esposto nella tua collezione pubblica. I visitatori possono ammirarne le fotografie e la storia di provenienza ininterrotta, ma non possono acquistarlo.',
      PRIVATE_ARCHIVE_DESC: 'Questo pezzo è custodito nel tuo archivio privato. È rigorosamente nascosto da tutte le ricerche pubbliche. Solo tu puoi vederlo.',
      CHANGE_STATUS: 'Modifica Visibilità e Stato'
    },
    MODAL_VISIBILITY: {
      TITLE: 'Gestisci Visibilità e Lignaggio dell\'Opera',
      RECOMMENDATION_TITLE: 'Raccomandazione di Lignaggio Vivente: Nascondi, Non Eliminare',
      RECOMMENDATION_BODY: 'Su Antiqry, ogni opera registrata conserva una cronologia storica ininterrotta. Anziché eliminare permanentemente e distruggere i suoi registri di provenienza, consigliamo di nascondere questo pezzo. Sarà ritirato dal mercato rimanendo custodito nel tuo archivio privato.',
      CHOICE_1_TITLE: 'Disponibile per la Vendita sul Mercato',
      CHOICE_1_DESC: 'Pubblicato con il tuo prezzo richiesto. Attivo nelle ricerche di mercato e aperto alle offerte e all\'acquisizione dei collezionisti.',
      CHOICE_1_BTN: 'Metti in Vendita',
      CHOICE_2_TITLE: 'Esposto in Collezione Pubblica',
      CHOICE_2_DESC: 'Esposto nella tua galleria pubblica. I visitatori possono ammirare le foto e verificare la provenienza, ma non possono acquistarlo (Non in Vendita).',
      CHOICE_2_BTN: 'Esponi (Non in Vendita)',
      CHOICE_3_TITLE: 'Archivio Privato (Nascosto al Pubblico)',
      CHOICE_3_DESC: 'Rigorosamente nascosto da ricerche pubbliche, cataloghi di vendita e collezioni. Solo tu (il proprietario) puoi accedervi.',
      CHOICE_3_BTN: 'Rendi Privato / Nascondi',
      DELETE_LINK: 'Desidero comunque eliminare definitivamente questo record →',
      DELETE_WARNING: 'L\'eliminazione definitiva è irreversibile. Tutte le tappe storiche e le perizie IA per questo pezzo andranno distrutte per sempre.',
      DELETE_CONFIRM: 'Conferma Eliminazione Definitiva'
    },
    ITEM: {
      ACQUISITION_VALUE: 'Valore di Acquisizione',
      ASKING_PRICE: 'Prezzo Richiesto',
      MAKE_OFFER: 'Fai un\'Offerta',
      ACQUIRE_NOW: 'Acquisisci Oggetto',
      PLACE_OFFER: 'Invia Offerta Vincolante',
      OFFER_SUCCESS: 'Offerta inviata con successo al collezionista.',
      PROVENANCE_LINEAGE: 'Lignaggio di Provenienza Verificato',
      RECORD_MILESTONE: 'Registra Tappa Storica',
      ADD_PROVENANCE_BTN: '+ Aggiungi Tappa di Provenienza',
      PEDIGREE_GUARANTEE_TITLE: 'Garanzia di Pedigree Ininterrotto',
      PEDIGREE_GUARANTEE_DESC: 'Quando un\'opera cambia proprietario su Antiqry, il suo ciclo vitale non termina mai. Tutta la documentazione, gli aneddoti storici, i restauri e le attestazioni dei passati possessori restano legati all\'opera per i futuri collezionisti.',
      CONDITION: 'Stato di Conservazione',
      MATERIALS: 'Materiali',
      CATEGORY: 'Categoria',
      CULTURE: 'Epoca / Cultura',
      MAKER: 'Autore / Bottega / Manifattura',
      CURATOR_NOTES: 'Note del Curatore',
      ERA: 'Epoca Stimata',
      HISTORICAL_DESCRIPTION: 'Descrizione Storica e Visiva',
      AI_APPRAISAL_REPORT: 'Relazione di Curatela & Autenticità IA Antiqry',
      RE_APPRAISE: 'Rivaluta con l\'IA',
      EDIT_LISTING: 'Modifica Scheda & Dettagli di Curatela',
      REPLACE_MANAGE_PHOTOS: 'Sostituisci / Gestisci Foto',
      UPLOAD_PHOTOS: 'Carica Fotografie',
      NO_PHOTOS_TITLE: 'Aggiungi Fotografie dell\'Oggetto',
      NO_PHOTOS_DESC: 'Carica fotografie nitide per mostrare la maestria artistica di questo pezzo.'
    },
    PHOTO_MODAL: {
      TITLE: 'Gestisci e Sostituisci le Fotografie dell\'Opera',
      CURRENT_PHOTOS: 'Foto Attualmente Selezionate',
      PRIMARY_COVER: '★ Copertina Principale',
      MAKE_PRIMARY: 'Imposta come Copertina',
      REPLACE_PHOTO: 'Sostituisci Foto',
      DELETE_PHOTO: 'Elimina Foto',
      SELECT_PHOTOS_BTN: 'Seleziona Foto dal Dispositivo',
      DROPZONE_TITLE: 'Aggiungi o Sostituisci Fotografie',
      DROPZONE_DESC: 'Carica foto nitide e ben illuminate (fronte, retro, dettagli, marchi). Supportata fotocamera da smartphone.',
      SAVE_PHOTOS: 'Salva Fotografie',
      SAVE_REAPPRAISE: 'Salva & Rivaluta con l\'IA'
    },
    CATEGORIES: {
      'Antiques': 'Antiquariato',
      'Art': 'Arte & Dipinti',
      'Watches & Horology': 'Orologeria & Misuratori del Tempo',
      'Jewellery & Gemstones': 'Gioielli & Gemme',
      'Fossils & Natural History': 'Fossili & Storia Naturale',
      'Coins & Numismatics': 'Monete & Numismatica',
      'Sculptures & Bronzes': 'Sculture & Bronzi',
      'Rare Books & Manuscripts': 'Libri Rari & Manoscritti',
      'Silver & Precious Metals': 'Argenteria & Metalli Preziosi',
      'Clocks & Scientific Instruments': 'Orologi da Arredo & Strumenti Scientifici',
      'Armor & Militaria': 'Armature & Militaria',
      'Archaeological Artifacts': 'Reperti Archeologici',
      'Appreciating Collectibles': 'Oggetti da Collezione Pregiati'
    },
    COMMERCIAL: {
      TITLE: 'Identità Commerciale & Galleria',
      NAME: 'Nome Commerciale / Galleria / Negozio',
      NAME_HINT: 'Nome ufficiale mostrato sulle opere catalogate, schede di galleria e cartellini di esposizione fisica.',
      ANONYMOUS_TOGGLE: 'Vendi ed Esponi in Forma Anonima (Modalità Discreta)',
      ANONYMOUS_HINT: 'Nascondi il tuo nome commerciale e l\'identità personale dalle pagine pubbliche. Il tuo stato verificato e i tuoi voti/valutazioni accumulati RIMARRANNO visibili agli acquirenti, garantendo totale fiducia senza esporre la tua identità.',
      CURATOR_TITLE: 'Galleria Espositrice / Antiquario',
      ANONYMOUS_SELLER: 'Antiquario Privato (Venditore Discreto)',
      VERIFIED_DEALER: 'Commerciante Verificato',
      VERIFIED_COLLECTOR: 'Collezionista Verificato',
      POSITIVE_FEEDBACK: 'feedback positivi',
      REVIEWS_COUNT: 'valutazioni verificate',
      NO_REVIEWS: 'Nuovo nel Registro'
    },
    QR_TAG: {
      BUTTON: 'Cartellino QR per Esposizione',
      MODAL_TITLE: 'Cartellino per Esposizione Museale e Galleria',
      MODAL_SUBTITLE: 'Stampa o posiziona questo cartellino accanto all\'oggetto nel tuo negozio, stand o vetrina.',
      SCAN_PROMPT: 'Inquadra con il telefono per verificare la provenienza ininterrotta e acquistare questo oggetto su Antiqry.',
      PRINT: 'Stampa Cartellino',
      CLOSE: 'Chiudi',
      REGISTRY_STANDARD: 'Standard di Provenienza Vivente Antiqry'
    },
    REVIEWS: {
      RATE_TRANSACTION: 'Valuta Transazione',
      RATE_SELLER: 'Valuta Venditore',
      RATE_BUYER: 'Valuta Acquirente',
      YOU_RATED: 'Hai valutato',
      MODAL_TITLE: 'Valutazione e Feedback della Transazione',
      MODAL_SUBTITLE: 'Valuta questa transazione per costruire reputazione nel registro di custodia viva Antiqry.',
      RATING_LABEL: 'Valutazione (da 1 a 5 Stelle)',
      COMMENT_LABEL: 'Commento ed Esperienza (Facoltativo)',
      COMMENT_PLACEHOLDER: 'Descrivi lo stato dell\'opera, l\'imballaggio, la documentazione storica, la spedizione...',
      SUBMIT: 'Invia Valutazione',
      SUCCESS: 'Grazie! La tua valutazione verificata è stata registrata.'
    },
    PREFERENCES: {
      TITLE: 'Preferenze Regionali e di Visualizzazione',
      SUBTITLE: 'Personalizza la tua lingua del registro e la valuta di valutazione del marketplace.',
      LANGUAGE_LABEL: 'Lingua Preferita dell\'Interfaccia',
      LANGUAGE_HINT: 'Utilizzata per la navigazione nel catalogo, cronologie e rapporti di provenienza.',
      CURRENCY_LABEL: 'Valuta di Visualizzazione e Valutazione',
      CURRENCY_HINT: 'Tutti i prezzi di vendita, le stime e i calcolatori verranno convertiti in questa valuta.',
      SAVED: 'Preferenze regionali aggiornate con successo.'
    },
    FOOTER: {
      TAGLINE: 'Il Registro Vivente Globale per Antiquariato, Belle Arti & Oggetti Rari da Collezione.',
      ALL_RIGHTS: 'Tutti i diritti riservati.',
      TERMS: 'Termini di Servizio',
      PRIVACY: 'Informativa sulla Privacy',
      PROVENANCE_STANDARDS: 'Standard di Provenienza'
    }
  }
};

@Injectable({
  providedIn: 'root'
})
export class I18nService {
  private readonly STORAGE_KEY = 'antiqry_lang';
  private currentLangSubject = new BehaviorSubject<string>('en');
  public currentLang$: Observable<string> = this.currentLangSubject.asObservable();

  public readonly languages = SUPPORTED_LANGUAGES;

  constructor() {
    this.initLanguage();
  }

  private initLanguage(): void {
    const saved = localStorage.getItem(this.STORAGE_KEY);
    if (saved && SUPPORTED_LANGUAGES.some(l => l.code === saved)) {
      this.currentLangSubject.next(saved);
      return;
    }

    // Auto-detect browser language
    try {
      const browserLang = (navigator.language || (navigator as any).userLanguage || 'en').substring(0, 2).toLowerCase();
      if (SUPPORTED_LANGUAGES.some(l => l.code === browserLang)) {
        this.setLanguage(browserLang);
        return;
      }
    } catch {
      // Ignore
    }

    this.currentLangSubject.next('en');
  }

  public get currentLanguage(): Language {
    const code = this.currentLangSubject.value;
    return SUPPORTED_LANGUAGES.find(l => l.code === code) || SUPPORTED_LANGUAGES[0];
  }

  public get currentLangCode(): string {
    return this.currentLangSubject.value;
  }

  public setLanguage(code: string): void {
    if (SUPPORTED_LANGUAGES.some(l => l.code === code)) {
      localStorage.setItem(this.STORAGE_KEY, code);
      this.currentLangSubject.next(code);
    }
  }

  /**
   * Resolves a dotted key (e.g. 'NAV.MARKETPLACE') to translated string.
   */
  public t(key: string, fallback?: string): string {
    if (!key) return fallback || '';
    const lang = this.currentLangSubject.value;
    const dict = TRANSLATIONS[lang] || TRANSLATIONS['en'];

    const parts = key.split('.');
    let val: any = dict;
    for (const p of parts) {
      if (val && typeof val === 'object' && p in val) {
        val = val[p];
      } else {
        val = null;
        break;
      }
    }

    if (typeof val === 'string' && val.trim().length > 0) {
      return val;
    }

    // Fallback to English dictionary
    if (lang !== 'en') {
      let enVal: any = TRANSLATIONS['en'];
      for (const p of parts) {
        if (enVal && typeof enVal === 'object' && p in enVal) {
          enVal = enVal[p];
        } else {
          enVal = null;
          break;
        }
      }
      if (typeof enVal === 'string' && enVal.trim().length > 0) {
        return enVal;
      }
    }

    return (fallback !== undefined && fallback.trim().length > 0) ? fallback : key;
  }

  /**
   * Translates a category name dynamically.
   */
  public translateCategory(catName: string): string {
    if (!catName) return '';
    const key = `CATEGORIES.${catName}`;
    const trans = this.t(key, catName);
    return trans !== key ? trans : catName;
  }
}
